import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  FormLabel
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Save as SaveIcon,
  Female as FemaleIcon,
  Male as MaleIcon,
  Transgender as TransgenderIcon,
  Close as CloseIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from './CustomDatePicker';
import { createApiUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/authUtils';
import { useSnackbar } from './CentralSnackbar';

const AddDemoPatientDialog = ({ open, onClose, onSuccess, editPatient = null }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const isEditMode = !!editPatient;
  
  // Form state - Patient Data
  const [patientData, setPatientData] = useState({
    firstName: '',
    lastName: '',
    patientId: '',
    birthDate: '',
    sex: '',
    insuranceStatus: 'gkv',
    emergencyStatus: 'normal'
  });
  
  // Form state - Exam Data
  const [examData, setExamData] = useState({
    accessionNumber: '',
    admissionId: '',
    modality: '',
    studyDescription: '',
    clinicalInfo: '',
    question: '',
    referringPhysician: '',
    observationDate: '',
    firstReader: null,
    tenant: '1',
    studyInstanceUid: '' // NEW: Demo-specific field
  });
  
  // Options for dropdowns
  const [studyDescriptions, setStudyDescriptions] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [referringPhysicians, setReferringPhysicians] = useState([]);
  const [examinations, setExaminations] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Load dropdown options on mount
  useEffect(() => {
    if (open) {
      loadDropdownData();
    }
  }, [open]);

  // Load patient data when editing
  useEffect(() => {
    if (open && editPatient) {
      // Parse patient name into first and last name
      const nameParts = editPatient.patient_name ? editPatient.patient_name.split(' ') : ['', ''];
      const firstName = nameParts.slice(0, -1).join(' ') || '';
      const lastName = nameParts[nameParts.length - 1] || '';
      
      // Format dates to YYYY-MM-DD
      const formatDate = (dateString) => {
        if (!dateString) return '';
        // If it's already in YYYY-MM-DD format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
        // Otherwise, try to parse and format
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return '';
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        } catch {
          return '';
        }
      };
      
      setPatientData({
        firstName: firstName,
        lastName: lastName,
        patientId: editPatient.patient_id || '',
        birthDate: formatDate(editPatient.birth_date),
        sex: editPatient.sex || '',
        insuranceStatus: editPatient.insurance_status || 'gkv',
        emergencyStatus: editPatient.emergency_status || 'normal'
      });
      
      // Find the first reader user object if ID is provided
      let firstReaderObj = null;
      if (editPatient.first_reader && users.length > 0) {
        firstReaderObj = users.find(u => u.id === editPatient.first_reader);
      }
      
      setExamData({
        accessionNumber: editPatient.accession_number || '',
        admissionId: editPatient.admission_id || '',
        modality: editPatient.modality || '',
        studyDescription: editPatient.study_description || '',
        clinicalInfo: editPatient.clinical_info || '',
        question: editPatient.question || '',
        referringPhysician: editPatient.referring_physician || '',
        observationDate: formatDate(editPatient.observation_date),
        firstReader: firstReaderObj || null,
        tenant: editPatient.tenant || '1',
        studyInstanceUid: editPatient.study_instance_uid || ''
      });
    } else if (open && !editPatient) {
      // Reset form for new patient
      setPatientData({
        firstName: '',
        lastName: '',
        patientId: '',
        birthDate: '',
        sex: '',
        insuranceStatus: 'gkv',
        emergencyStatus: 'normal'
      });
      
      setExamData({
        accessionNumber: '',
        admissionId: '',
        modality: '',
        studyDescription: '',
        clinicalInfo: '',
        question: '',
        referringPhysician: '',
        observationDate: '',
        firstReader: null,
        tenant: '1',
        studyInstanceUid: ''
      });
    }
  }, [open, editPatient, users]);

  // Filter study descriptions when modality changes
  useEffect(() => {
    if (examData.modality && examinations.length > 0) {
      const filteredExams = examinations.filter(e => e.modality === examData.modality);
      const filteredDescriptions = [...new Set(filteredExams.map(e => e.name).filter(Boolean))].sort();
      setStudyDescriptions(filteredDescriptions);
      
      if (examData.studyDescription && !filteredDescriptions.includes(examData.studyDescription)) {
        setExamData(prev => ({ ...prev, studyDescription: '' }));
      }
    } else if (examinations.length > 0) {
      const allDescriptions = [...new Set(examinations.map(e => e.name).filter(Boolean))].sort();
      setStudyDescriptions(allDescriptions);
    }
  }, [examData.modality, examinations]);

  const loadDropdownData = async () => {
    try {
      setLoadingData(true);
      
      // Load examinations
      const examinationsResponse = await fetch(createApiUrl('admin/examinations'), {
        headers: getAuthHeaders()
      });
      
      if (examinationsResponse.ok) {
        const examinationsData = await examinationsResponse.json();
        setExaminations(examinationsData);
        const uniqueDescriptions = [...new Set(examinationsData.map(e => e.name).filter(Boolean))].sort();
        setStudyDescriptions(uniqueDescriptions);
        const uniqueModalities = [...new Set(examinationsData.map(e => e.modality).filter(Boolean))].sort();
        setModalities(uniqueModalities);
      }
      
      // Load users
      const usersResponse = await fetch(createApiUrl('users'), {
        headers: getAuthHeaders()
      });
      
      if (usersResponse.ok) {
        const allUsers = await usersResponse.json();
        const filteredUsers = allUsers
          .filter(u => u.group_level >= 1 && u.group_level <= 5 && u.is_active === true)
          .map(u => ({
            id: u.id,
            label: [u.academic_degree, u.first_name, u.last_name]
              .filter(Boolean)
              .join(' ') || u.username,
            username: u.username
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        
        setUsers(filteredUsers);
      }
      
      // Load tenants
      const tenantsResponse = await fetch(createApiUrl('admin/tenants'), {
        headers: getAuthHeaders()
      });
      
      if (tenantsResponse.ok) {
        const tenantsData = await tenantsResponse.json();
        setTenants(tenantsData.filter(t => t.is_active));
      }
      
      // Load referring physicians
      const physiciansResponse = await fetch(createApiUrl('referring-physicians'), {
        headers: getAuthHeaders()
      });
      
      if (physiciansResponse.ok) {
        const physiciansData = await physiciansResponse.json();
        setReferringPhysicians(physiciansData);
      }
      
    } catch (error) {
      console.error('Fehler beim Laden der Dropdown-Daten:', error);
      const errorMsg = 'Fehler beim Laden der Formulardaten';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoadingData(false);
    }
  };

  const handlePatientDataChange = (field, value) => {
    setPatientData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleExamDataChange = (field, value) => {
    setExamData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!patientData.firstName || !patientData.lastName) {
      const errorMsg = t('demo.messages.firstNameRequired');
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!patientData.birthDate) {
      const errorMsg = t('demo.messages.birthDateRequired');
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!patientData.sex) {
      const errorMsg = t('demo.messages.sexRequired');
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!patientData.patientId) {
      const errorMsg = t('demo.messages.patientIdRequired');
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!examData.modality) {
      const errorMsg = t('demo.messages.modalityRequired');
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!examData.studyDescription) {
      const errorMsg = t('demo.messages.studyDescriptionRequired');
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!examData.observationDate) {
      const errorMsg = t('demo.messages.observationDateRequired');
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!examData.referringPhysician) {
      const errorMsg = t('demo.messages.referringPhysicianRequired');
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!examData.accessionNumber || !examData.admissionId) {
      const errorMsg = t('demo.messages.accessionRequired');
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!examData.clinicalInfo) {
      const errorMsg = t('demo.messages.clinicalInfoRequired');
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!examData.question) {
      const errorMsg = t('demo.messages.questionRequired');
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!examData.studyInstanceUid) {
      const errorMsg = t('demo.messages.studyInstanceUidRequired');
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    if (!examData.tenant) {
      const errorMsg = t('demo.messages.tenantRequired');
      setError(errorMsg);
      showError(errorMsg);
      return false;
    }
    
    return true;
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!editPatient) return;
    
    setDeleteConfirmOpen(false);
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(createApiUrl(`demo-patients/${editPatient.id}`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showSuccess(t('demo.messages.deleteSuccess'));
        onSuccess();
        onClose();
      } else {
        const errorMsg = data.message || t('demo.messages.deleteError');
        setError(errorMsg);
        showError(errorMsg);
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      const errorMsg = t('demo.messages.deleteError');
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      let firstReaderId = null;
      if (examData.firstReader) {
        if (typeof examData.firstReader === 'object' && examData.firstReader.id) {
          firstReaderId = examData.firstReader.id;
        }
      }
      
      const payload = {
        patient_id: patientData.patientId,
        first_name: patientData.firstName,
        last_name: patientData.lastName,
        birth_date: patientData.birthDate,
        sex: patientData.sex,
        insurance_status: patientData.insuranceStatus,
        emergency_status: patientData.emergencyStatus,
        accession_number: examData.accessionNumber,
        admission_id: examData.admissionId,
        modality: examData.modality,
        study_description: examData.studyDescription,
        clinical_info: examData.clinicalInfo,
        question: examData.question,
        referring_physician: examData.referringPhysician,
        observation_date: examData.observationDate,
        first_reader: firstReaderId,
        tenant: examData.tenant,
        study_instance_uid: examData.studyInstanceUid
      };
      
      const url = isEditMode ? `demo-patients/${editPatient.id}` : 'demo-patients';
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(createApiUrl(url), {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showSuccess(isEditMode ? t('demo.messages.updateSuccess') : t('demo.messages.createSuccess'));
        
        // Reset form
        setPatientData({
          firstName: '',
          lastName: '',
          patientId: '',
          birthDate: '',
          sex: '',
          insuranceStatus: 'gkv',
          emergencyStatus: 'normal'
        });
        
        setExamData({
          accessionNumber: '',
          admissionId: '',
          modality: '',
          studyDescription: '',
          clinicalInfo: '',
          question: '',
          referringPhysician: '',
          observationDate: '',
          firstReader: null,
          tenant: examData.tenant,
          studyInstanceUid: ''
        });
        
        onSuccess();
        onClose();
      } else {
        const errorMsg = data.message || (isEditMode ? t('demo.messages.updateError') : t('demo.messages.createError'));
        setError(errorMsg);
        showError(errorMsg);
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      const errorMsg = isEditMode ? t('demo.messages.networkErrorUpdate') : t('demo.messages.networkError');
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon sx={{ color: 'var(--rd-primary)' }} />
          {isEditMode ? t('demo.dialog.titleEdit') : t('demo.dialog.titleAdd')}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Left Column - Patient Data */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 3, color: 'var(--rd-primary)', fontWeight: 'bold' }}>
                  {t('addPatient.patientData')}
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Patienten-ID* */}
                  <TextField
                    fullWidth
                    label={t('addPatient.patientId', 'Patienten-ID')}
                    value={patientData.patientId}
                    onChange={(e) => handlePatientDataChange('patientId', e.target.value)}
                    required
                    className="textfield"
                  />
                  
                  {/* Vorname, Nachname* */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label={t('addPatient.firstName', 'Vorname')}
                      value={patientData.firstName}
                      onChange={(e) => handlePatientDataChange('firstName', e.target.value)}
                      required
                      className="textfield"
                    />
                    <TextField
                      fullWidth
                      label={t('addPatient.lastName', 'Nachname')}
                      value={patientData.lastName}
                      onChange={(e) => handlePatientDataChange('lastName', e.target.value)}
                      required
                      className="textfield"
                    />
                  </Box>
                  
                  {/* Geschlecht */}
                  <Box>
                    <FormLabel required sx={{ mb: 1, display: 'block', fontSize: '0.875rem' }}>
                      {t('addPatient.sex', 'Geschlecht')}
                    </FormLabel>
                    <ToggleButtonGroup
                      value={patientData.sex}
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue !== null) {
                          handlePatientDataChange('sex', newValue);
                        }
                      }}
                      sx={{ width: '100%' }}
                    >
                      <ToggleButton value="männlich" sx={{ flex: 1, color: 'grey.500', '&.Mui-selected': { color: 'var(--rd-primary) !important', backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent) !important', borderColor: 'var(--rd-primary) !important' }}}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <MaleIcon />
                          <Typography variant="caption">{t('addPatient.male', 'Männlich')}</Typography>
                        </Box>
                      </ToggleButton>
                      <ToggleButton value="weiblich" sx={{ flex: 1, color: 'grey.500', '&.Mui-selected': { color: 'var(--rd-primary) !important', backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent) !important', borderColor: 'var(--rd-primary) !important' }}}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <FemaleIcon />
                          <Typography variant="caption">{t('addPatient.female', 'Weiblich')}</Typography>
                        </Box>
                      </ToggleButton>
                      <ToggleButton value="divers" sx={{ flex: 1, color: 'grey.500', '&.Mui-selected': { color: 'var(--rd-primary) !important', backgroundColor: 'color-mix(in srgb, var(--rd-primary) 8%, transparent) !important', borderColor: 'var(--rd-primary) !important' }}}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <TransgenderIcon />
                          <Typography variant="caption">{t('addPatient.diverse', 'Divers')}</Typography>
                        </Box>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  
                  {/* Geburtsdatum* */}
                  <CustomDatePicker
                    label={t('addPatient.birthDate', 'Geburtsdatum')}
                    value={patientData.birthDate}
                    onChange={(e) => handlePatientDataChange('birthDate', e.target.value)}
                    required
                    sx={{ width: '100%', '& .MuiOutlinedInput-root': { height: '40px', minHeight: '40px' }, '& .MuiOutlinedInput-input': { padding: '8.5px 14px', height: '23px' }}}
                  />

                  <Box>
                    <FormLabel sx={{ mb: 1, display: 'block', fontSize: '0.875rem' }}>
                      {t('report.insuranceStatus.label', 'Versicherungsstatus')}
                    </FormLabel>
                    <ToggleButtonGroup
                      value={patientData.insuranceStatus}
                      exclusive
                      onChange={(e, val) => val && handlePatientDataChange('insuranceStatus', val)}
                      size="small"
                      sx={{ flexWrap: 'wrap', gap: 0.5 }}
                    >
                      <ToggleButton value="pkv">{t('report.insuranceStatus.pkv')}</ToggleButton>
                      <ToggleButton value="gkv">{t('report.insuranceStatus.gkv')}</ToggleButton>
                      <ToggleButton value="bg">{t('report.insuranceStatus.bg')}</ToggleButton>
                      <ToggleButton value="sz">{t('report.insuranceStatus.sz')}</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <Box>
                    <FormLabel sx={{ mb: 1, display: 'block', fontSize: '0.875rem' }}>
                      {t('report.emergencyStatus.label', 'Dringlichkeit')}
                    </FormLabel>
                    <ToggleButtonGroup
                      value={patientData.emergencyStatus}
                      exclusive
                      onChange={(e, val) => val && handlePatientDataChange('emergencyStatus', val)}
                      size="small"
                    >
                      <ToggleButton value="normal">{t('report.emergencyStatus.normal')}</ToggleButton>
                      <ToggleButton value="urgent">{t('report.emergencyStatus.urgent')}</ToggleButton>
                      <ToggleButton value="emergency">{t('report.emergencyStatus.emergency')}</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Right Column - Exam Data */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 3, color: 'var(--rd-primary)', fontWeight: 'bold' }}>
                  {t('addPatient.examData')}
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Mandant, Zuweiser* */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {tenants.length > 1 && (
                      <Autocomplete
                        freeSolo
                        options={tenants}
                        value={tenants.find(t => t.id.toString() === examData.tenant) || null}
                        onChange={(e, newValue) => { if (newValue && newValue.id) handleExamDataChange('tenant', newValue.id.toString()); }}
                        getOptionLabel={(option) => option?.bezeichnung || ''}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        sx={{ flex: 1 }}
                        renderInput={(params) => (<TextField {...params} label={t('addPatient.tenant', 'Mandant')} required className="textfield" />)}
                      />
                    )}
                    <Autocomplete
                      freeSolo
                      options={referringPhysicians}
                      value={examData.referringPhysician}
                      onChange={(e, newValue) => handleExamDataChange('referringPhysician', newValue || '')}
                      onInputChange={(e, newValue) => handleExamDataChange('referringPhysician', newValue)}
                      sx={{ flex: 2 }}
                      renderInput={(params) => (<TextField {...params} label={t('addPatient.referringPhysician', 'Zuweiser')} required className="textfield" />)}
                    />
                  </Box>
                  
                  {/* Untersuchungsdatum* */}
                  <CustomDatePicker
                    label={t('addPatient.observationDate', 'Untersuchungsdatum')}
                    value={examData.observationDate}
                    onChange={(e) => handleExamDataChange('observationDate', e.target.value)}
                    required
                    sx={{ width: '100%', '& .MuiOutlinedInput-root': { height: '40px', minHeight: '40px' }, '& .MuiOutlinedInput-input': { padding: '8.5px 14px', height: '23px' }}}
                  />
                  
                  {/* Accession Number, Aufnahme ID* */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label={t('addPatient.accessionNumber', 'Accession Number')}
                      value={examData.accessionNumber}
                      onChange={(e) => handleExamDataChange('accessionNumber', e.target.value)}
                      required
                      className="textfield"
                    />
                    <TextField
                      fullWidth
                      label={t('addPatient.admissionId', 'Aufnahme-ID')}
                      value={examData.admissionId}
                      onChange={(e) => handleExamDataChange('admissionId', e.target.value)}
                      required
                      className="textfield"
                    />
                  </Box>
                  
                  {/* Modalität, Studienbeschreibung* */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Autocomplete
                      freeSolo
                      options={modalities}
                      value={examData.modality}
                      onChange={(e, newValue) => handleExamDataChange('modality', newValue || '')}
                      onInputChange={(e, newValue) => handleExamDataChange('modality', newValue)}
                      sx={{ flex: 1 }}
                      renderInput={(params) => (<TextField {...params} label={t('addPatient.modality', 'Modalität')} required className="textfield" />)}
                    />
                    <Autocomplete
                      freeSolo
                      options={studyDescriptions}
                      value={examData.studyDescription}
                      onChange={(e, newValue) => handleExamDataChange('studyDescription', newValue || '')}
                      onInputChange={(e, newValue) => handleExamDataChange('studyDescription', newValue)}
                      sx={{ flex: 2 }}
                      renderInput={(params) => (<TextField {...params} label={t('addPatient.studyDescription', 'Studienbeschreibung')} required className="textfield" />)}
                    />
                  </Box>
                  
                  {/* Klinische Informationen */}
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t('addPatient.clinicalInfo', 'Klinische Informationen')}
                    value={examData.clinicalInfo}
                    onChange={(e) => handleExamDataChange('clinicalInfo', e.target.value)}
                    required
                    className="textfield"
                  />
                  
                  {/* Fragestellung */}
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t('addPatient.question', 'Fragestellung')}
                    value={examData.question}
                    onChange={(e) => handleExamDataChange('question', e.target.value)}
                    required
                    className="textfield"
                  />
                  
                  {/* Erstbefunder */}
                  <Autocomplete
                    freeSolo
                    options={users}
                    value={examData.firstReader}
                    onChange={(e, newValue) => { if (typeof newValue === 'string') { handleExamDataChange('firstReader', { id: null, label: newValue }); } else { handleExamDataChange('firstReader', newValue); }}}
                    onInputChange={(e, newValue, reason) => { if (reason === 'input') { handleExamDataChange('firstReader', newValue ? { id: null, label: newValue } : null); }}}
                    getOptionLabel={(option) => { if (typeof option === 'string') return option; return option?.label || ''; }}
                    isOptionEqualToValue={(option, value) => { if (!option || !value) return false; return option.id === value.id; }}
                    renderInput={(params) => (<TextField {...params} label={t('addPatient.firstReader', 'Erstbefunder')} className="textfield" />)}
                  />
                  
                  {/* Study Instance UID */}
                  <TextField
                    fullWidth
                    label={t('demo.dialog.studyInstanceUid')}
                    value={examData.studyInstanceUid}
                    onChange={(e) => handleExamDataChange('studyInstanceUid', e.target.value)}
                    required
                    className="textfield"
                    helperText={t('demo.dialog.studyInstanceUidHelper')}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
        {/* Delete Button - Bottom Left (only in edit mode) */}
        {isEditMode ? (
          <Button 
            onClick={handleDeleteClick} 
            disabled={loading}
            sx={{
              backgroundColor: 'var(--rd-error)',
              color: 'white',
              '&:hover': { backgroundColor: 'var(--rd-danger)' },
              '&:disabled': { backgroundColor: 'grey.400', color: 'white' }
            }}
            startIcon={<DeleteIcon />}
          >
            {t('demo.dialog.delete')}
          </Button>
        ) : (
          <Box /> /* Empty box to maintain spacing */
        )}
        
        {/* Cancel and Save/Update Buttons - Bottom Right */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} className="button-cancel" disabled={loading} startIcon={<CloseIcon />}>
            {t('demo.dialog.cancel')}
          </Button>
          <Button onClick={handleSubmit} className="button" disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}>
            {loading 
              ? (isEditMode ? t('demo.dialog.updating') : t('demo.dialog.saving')) 
              : (isEditMode ? t('demo.dialog.update') : t('demo.dialog.save'))
            }
          </Button>
        </Box>
      </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
      open={deleteConfirmOpen}
      onClose={handleDeleteCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {t('demo.dialog.delete')}
      </DialogTitle>
      <DialogContent>
        <Typography>
          {t('demo.messages.deleteConfirm')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleDeleteCancel} className="button-cancel" startIcon={<CloseIcon />}>
          {t('demo.dialog.cancel')}
        </Button>
        <Button 
          onClick={handleDeleteConfirm} 
          disabled={loading}
          sx={{
            backgroundColor: 'var(--rd-error)',
            color: 'white',
            '&:hover': { backgroundColor: 'var(--rd-danger)' },
            '&:disabled': { backgroundColor: 'grey.400', color: 'white' }
          }}
          startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {loading ? t('demo.dialog.deleting') : t('demo.dialog.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default AddDemoPatientDialog;
